import React, { FunctionComponent } from 'react';
import moment from 'moment';
import {
  Plans,
  Customer,
  SubscriptionSchedules,
  Prices,
} from '../../../../deprecated/payment/types';
import styles from './update-subscription-preview.module.scss';
import AllSubscriptions from 'src/models/AllSubscriptions';
import {
  getCurrencySymbol,
  checkNZUser,
  isUserFromNewZealand,
  isUserFromUK,
  isChallengeBundleActive,
  detectActiveCurrency,
} from 'src/lib/utils';
import { useSelector } from 'react-redux';
import { IAppState } from 'src/store';
import SubscriptionDiscountDisplay from './subscription-discount-display';

interface OwnProps {
  selectedPlan: string;
  plans: Plans | undefined;
  prices: Prices | undefined;
  customer: Customer | undefined;
  paused: boolean;
  subscriptionSchedules: SubscriptionSchedules[] | undefined;
  allSubscriptions: AllSubscriptions | undefined;
  isReactivateAllowed: boolean;
}

const UpdateSubscriptionPreview: FunctionComponent<OwnProps> = ({
  selectedPlan,
  plans,
  prices,
  customer,
  paused,
  subscriptionSchedules,
  allSubscriptions,
  isReactivateAllowed,
}) => {
  const user = useSelector((state: IAppState) => state.auth?.user);

  const subscriptionToShow = !!allSubscriptions?.trialSubscription
    ? allSubscriptions.trialSubscription
    : allSubscriptions?.currentSubscription;
  let nextBillingDateUnix;
  if (subscriptionSchedules && subscriptionSchedules.length > 0 && prices) {
    nextBillingDateUnix = subscriptionSchedules[0].phases[0].start_date;
  } else if (customer && customer.subscriptions?.data?.length > 0) {
    nextBillingDateUnix = customer?.subscriptions?.data[0]?.current_period_end;
  }

  const activeCurrency = detectActiveCurrency(allSubscriptions);

  const isNewZealandUser = activeCurrency
    ? activeCurrency === 'nzd'
    : !isChallengeBundleActive() &&
      (isUserFromNewZealand() || (user?.country ?? '') === 'New Zealand');

  const isUKUser = activeCurrency ? activeCurrency === 'gbp' : isUserFromUK();

  const currencySymbol = getCurrencySymbol(
    activeCurrency || isNewZealandUser,
    isUKUser,
    true
  );

  // Pricing logic
  let fullAmount = 0;
  if (activeCurrency === 'nzd') {
    fullAmount =
      (subscriptionToShow?.currencyOptions?.nzd?.unit_amount || 1) / 100;
  } else if (activeCurrency === 'gbp') {
    fullAmount =
      (subscriptionToShow?.currencyOptions?.gbp?.unit_amount || 1) / 100;
  } else if (activeCurrency === 'aud') {
    fullAmount =
      (subscriptionToShow?.currencyOptions?.aud?.unit_amount || 1) / 100;
  } else {
    fullAmount = checkNZUser()
      ? (subscriptionToShow?.currencyOptions?.nzd?.unit_amount || 1) / 100
      : subscriptionToShow?.totalPrice
      ? parseFloat(subscriptionToShow?.totalPrice)
      : 0;
  }

  const nextPricingDisplay = () => {
    if (
      subscriptionToShow?.challengePackage &&
      allSubscriptions?.upcomingSubscription
    ) {
      if (allSubscriptions?.upcomingSubscription?.invoiceDiscount) {
        return (
          <>
            Billed{' '}
            {currencySymbol +
              Number(subscriptionToShow?.challengePackage?.price).toFixed(2)}
          </>
        );
      }
      return (
        <>
          {currencySymbol +
            Number(subscriptionToShow?.challengePackage?.price).toFixed(2)}
          , then{' '}
          {`${
            currencySymbol +
            (activeCurrency === 'nzd'
              ? (
                  (allSubscriptions?.upcomingSubscription?.currencyOptions?.nzd
                    ?.unit_amount || 1) / 100
                ).toString()
              : activeCurrency === 'gbp'
              ? (
                  (allSubscriptions?.upcomingSubscription?.currencyOptions?.gbp
                    ?.unit_amount || 1) / 100
                ).toString()
              : activeCurrency === 'aud'
              ? (
                  (allSubscriptions?.upcomingSubscription?.currencyOptions?.aud
                    ?.unit_amount || 1) / 100
                ).toString()
              : isNewZealandUser
              ? (
                  (allSubscriptions?.upcomingSubscription?.currencyOptions?.nzd
                    ?.unit_amount || 1) / 100
                ).toString()
              : allSubscriptions?.upcomingSubscription?.price.toFixed(2) || '')
          } every `}
          {(allSubscriptions?.upcomingSubscription?.intervalCount || 0) > 1
            ? allSubscriptions?.upcomingSubscription?.intervalCount + ' months'
            : 'month'}
        </>
      );
    }

    return (
      <SubscriptionDiscountDisplay
        invoiceDiscount={subscriptionToShow?.invoiceDiscount}
        pricing={{
          fullAmount,
          intervalCount: subscriptionToShow?.intervalCount ?? 1,
          interval: subscriptionToShow?.interval ?? 'month',
        }}
        isNewZealandUser={activeCurrency === 'nzd' || isNewZealandUser}
        currencySymbol={currencySymbol}
        hideThenPart={!!allSubscriptions?.upcomingSubscription?.invoiceDiscount}
      />
    );
  };

  return (
    <>
      {selectedPlan === '' || !plans || !allSubscriptions ? (
        <div className={styles.noneSelected}>No plan selected yet.</div>
      ) : !allSubscriptions ? (
        <div className={styles.changeSubscriptionPreview}>
          {!!nextBillingDateUnix && !paused && (
            <div className={styles.nextDate}>
              Next billing date is on{' '}
              {moment(nextBillingDateUnix * 1000).format('DD/MM/YYYY')}
            </div>
          )}
          {paused && <div className={styles.nextDate}>Subscription Paused</div>}
        </div>
      ) : (
        <div className={styles.changeSubscriptionPreview}>
          <div className={styles.price}>
            <div className={styles.big}>{subscriptionToShow?.planName} </div>
            {subscriptionToShow?.challengePackage?.planName ? (
              <div className={styles.challengeName}>
                {subscriptionToShow?.challengePackage?.planName}{' '}
              </div>
            ) : null}
            <div className={styles.nextDate}>{nextPricingDisplay()}</div>
            {isReactivateAllowed && user?.cancellationDate ? (
              <div className={styles.billingDate}>
                Your subscription will end on{' '}
                {moment(user.cancellationDate).format('DD/MM/YYYY')}
              </div>
            ) : (
              !!subscriptionToShow?.nextBillingDate && (
                <div className={styles.billingDate}>
                  Next billing date is on{' '}
                  {moment(subscriptionToShow?.nextBillingDate * 1000).format(
                    'DD/MM/YYYY'
                  )}
                </div>
              )
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default UpdateSubscriptionPreview;
